import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { App, Button, Form, Input, Select } from "antd";
import { observer } from "mobx-react";
import { useState } from "react";
import { LicenseAllocationField } from "./licenseAllocationField.js";
import { LicenseAllocationUpgradePromo } from "./licenseAllocationUpgradePromo.js";
import { RealCommerceError } from "../../../../../../exceptions/realCommerceError.js";
import { useStores } from "../../../../../../store/stores.js";
import { __ } from "../../../../../../utils/i18n.js";
import { MATOMO_MASK } from "../../../../../../utils/matomoMask.js";
import { useAccountV2ErrorHandling } from "../../../../hooks/useAccountV2ErrorHandling.js";
import { usePlanCapacity } from "../../../../hooks/usePlanCapacity.js";
const LicenseKeyAddEditForm = observer(({ subscription, license, mode, onCreated, onDone, form, onBusyChange })=>{
    const { message } = App.useApp();
    const { accountV2Store } = useStores();
    const productVariants = subscription.productVariants ?? [];
    const [isBusy, setIsBusy] = useState(false);
    const handleRealCommerceError = useAccountV2ErrorHandling();
    // Live capacity — re-evaluates on store refetch so an external upgrade
    // (e.g. the in-modal Upgrade-plan upsell) reflects new seats immediately
    // without remounting the form. The exhaustion-promo flash that the old
    // snapshot prevented is now gated on `!isBusy` at the promo render below.
    const capacity = usePlanCapacity(subscription, license);
    const updateBusy = (next)=>{
        setIsBusy(next);
        onBusyChange?.(next);
    };
    const initialValues = license ? {
        rpmProductVariantId: license.data.productVariant.id,
        installationsProduction: license.data.maxInstallationsProduction,
        installationsDevelopment: license.data.maxInstallationsDevelopment,
        description: license.data.description || ""
    } : {
        rpmProductVariantId: productVariants.length === 1 ? productVariants[0].data.id : "",
        installationsProduction: 1,
        installationsDevelopment: 1,
        description: ""
    };
    const onFinish = async (values)=>{
        updateBusy(true);
        // For single-variant subscriptions the product Form.Item isn't rendered,
        // so AntD never registers the field and submits an empty string. Fall
        // back to the only available variant id (or the edited license's id)
        // so the backend always receives a valid `rpmProductVariantId`.
        const rpmProductVariantId = values.rpmProductVariantId || license?.data.productVariant.id || (productVariants.length === 1 ? productVariants[0].data.id : "");
        const payload = {
            ...values,
            rpmProductVariantId
        };
        try {
            if (mode === "edit" && license) {
                await accountV2Store.editLicense(license, payload);
                message.success(__("License key updated."));
            } else {
                const created = await accountV2Store.addLicense(subscription, payload);
                if (created) {
                    onCreated?.(created);
                }
            }
            form.resetFields();
            onDone();
        } catch (e) {
            if (e instanceof RealCommerceError) {
                handleRealCommerceError(e);
            }
        } finally{
            updateBusy(false);
        }
    };
    const exhaustedPools = [
        capacity.isProductionExhausted ? "production" : null,
        capacity.isDevelopmentExhausted ? "development" : null
    ].filter((p)=>p !== null);
    return /*#__PURE__*/ _jsxs(Form, {
        form: form,
        layout: "vertical",
        initialValues: initialValues,
        onFinish: onFinish,
        disabled: isBusy,
        className: "rc-account-v2-license-form",
        children: [
            mode === "add" && /*#__PURE__*/ _jsx("p", {
                className: "rc-account-v2-license-form__intro",
                children: __("Split your licenses across multiple license keys - for example one license key per website or per environment. You can remove license keys any time.")
            }),
            mode === "edit" && license && /*#__PURE__*/ _jsx(Form.Item, {
                label: __("License key"),
                children: /*#__PURE__*/ _jsx(Input, {
                    value: license.data.licenseKey,
                    disabled: true,
                    ...MATOMO_MASK
                })
            }),
            /*#__PURE__*/ _jsx(Form.Item, {
                label: __("Name (optional)"),
                name: "description",
                rules: [
                    {
                        max: 200,
                        message: __("Please use a shorter name.")
                    }
                ],
                extra: __("Internal label - never shown inside the WordPress plugin."),
                children: /*#__PURE__*/ _jsx(Input, {
                    placeholder: __("e.g. the client name or the website domain")
                })
            }),
            productVariants.length > 1 && /*#__PURE__*/ _jsx(Form.Item, {
                label: __("Product"),
                name: "rpmProductVariantId",
                rules: [
                    {
                        required: true,
                        message: __("Please select a product.")
                    }
                ],
                children: /*#__PURE__*/ _jsx(Select, {
                    disabled: mode === "edit",
                    children: productVariants.map(({ data: { id, name } })=>/*#__PURE__*/ _jsx(Select.Option, {
                            value: id,
                            children: name
                        }, id))
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "rc-account-v2-license-alloc-section",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "rc-account-v2-license-alloc-section__heading",
                        children: __("Capacity for this license key")
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "rc-account-v2-license-alloc-section__fields",
                        children: [
                            /*#__PURE__*/ _jsx(LicenseAllocationField, {
                                name: "installationsProduction",
                                label: __("Production websites"),
                                variant: "production",
                                free: capacity.productionFree,
                                total: capacity.productionTotal,
                                disabled: isBusy
                            }),
                            /*#__PURE__*/ _jsx(LicenseAllocationField, {
                                name: "installationsDevelopment",
                                label: __("Development websites"),
                                variant: "development",
                                free: capacity.developmentFree,
                                total: capacity.developmentTotal,
                                disabled: isBusy
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "rc-account-v2-license-alloc-section__description",
                        children: __("Production websites serve real visitors. Development websites are staging or in-progress copies. The plugin works the same on both, though development sites may show a small license notice.")
                    })
                ]
            }),
            !isBusy && exhaustedPools.length > 0 && /*#__PURE__*/ _jsx("div", {
                style: {
                    marginTop: 16
                },
                children: /*#__PURE__*/ _jsx(LicenseAllocationUpgradePromo, {
                    productName: subscription.data.productPlan.product.name,
                    exhaustedPools: exhaustedPools,
                    onUpgradeClick: ()=>accountV2Store.openChangePlanFor(subscription, {
                            autoSelectFirstUpgrade: true
                        })
                })
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "rc-account-v2-license-modal-footer",
                children: /*#__PURE__*/ _jsx(Button, {
                    type: "primary",
                    loading: isBusy,
                    onClick: ()=>form.submit(),
                    children: mode === "edit" ? __("Save changes") : __("Create license key")
                })
            })
        ]
    });
});
export { LicenseKeyAddEditForm };
